import { useEffect, useState } from "react";
import api, { fmtMXN, downloadBlob, todayStr } from "../lib/api";
import Topbar from "../components/Topbar";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";
import { AddSupplierModal } from "../components/AddSupplierModal";
import { useAuth } from "../lib/auth";
import { Search, CheckCircle2, XCircle, Eye, Pause, Play, Building2, KeyRound, Copy, FileText, ExternalLink, Package, Wrench, Download, Plus, Store, AlertTriangle, Archive, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function Suppliers() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [accountModal, setAccountModal] = useState(null);
  const [approveModal, setApproveModal] = useState(null); // supplier being approved
  const [loginEmail, setLoginEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [docsModal, setDocsModal] = useState(null); // { supplier, docs }
  const [exporting, setExporting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [onlyPending, setOnlyPending] = useState(false);
  const [toArchive, setToArchive] = useState(null);   // proveedor en baja
  const [archiveReason, setArchiveReason] = useState("");
  const [toRestore, setToRestore] = useState(null);   // proveedor a restaurar
  const [restoreReason, setRestoreReason] = useState("");

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.set("supplier_type", typeFilter);
      const res = await api.get(`/suppliers/export?${params.toString()}`, { responseType: "blob" });
      downloadBlob(res.data, `proveedores_${todayStr()}.xlsx`);
      toast.success(t("export.success"));
    } catch {
      toast.error(t("export.error"));
    } finally {
      setExporting(false);
    }
  };

  const load = () => {
    const params = new URLSearchParams();
    if (typeFilter !== "all") params.set("supplier_type", typeFilter);
    // Los archivados solo se piden explícitamente (corporate_admin) para el filtro "Archivados".
    if (filter === "archived" && user?.role === "corporate_admin") params.set("include_archived", "true");
    return api.get(`/suppliers?${params.toString()}`).then((r) => setList(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter, filter]);

  const submitArchive = async () => {
    if (archiveReason.trim().length < 10) { toast.error(t("suppliers.archiveReasonError")); return; }
    setBusy(true);
    try {
      await api.post(`/suppliers/${toArchive.id}/archive`, { reason: archiveReason.trim() });
      toast.success(t("suppliers.archivedToast"));
      setToArchive(null); setArchiveReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const submitRestore = async () => {
    if (restoreReason.trim().length < 10) { toast.error(t("suppliers.archiveReasonError")); return; }
    setBusy(true);
    try {
      await api.post(`/suppliers/${toRestore.id}/restore`, { reason: restoreReason.trim() });
      toast.success(t("suppliers.restoredToast"));
      setToRestore(null); setRestoreReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const openDocs = async (s) => {
    setDocsModal({ supplier: s, docs: null });
    try {
      const r = await api.get(`/suppliers/${s.id}/documents`);
      setDocsModal({ supplier: s, docs: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
      setDocsModal(null);
    }
  };

  const viewDoc = async (doc) => {
    try {
      const r = await api.get(`/uploads/supplier-document/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const decide = async (id, action) => {
    const statusMap = { approve: "approved", reject: "rejected", suspend: "suspended" };
    try {
      await api.post(`/suppliers/${id}/approve`, { status: statusMap[action] });
      toast.success(action === "reject" ? t("suppliers.rejectedToast") : t("suppliers.suspendedToast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const openApprove = (s) => {
    setApproveModal(s);
    setLoginEmail(s.contact_email || "");
  };

  const confirmApprove = async () => {
    const email = loginEmail.trim().toLowerCase();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error(t("suppliers.invalidLoginEmail"));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/suppliers/${approveModal.id}/approve`, { status: "approved", email_login: email });
      toast.success(t("suppliers.approvedToast"));
      setApproveModal(null);
      if (r.data?.supplier_account?.created) {
        setAccountModal(r.data.supplier_account);
      } else if (r.data?.supplier_account?.already_exists) {
        toast.info(t("suppliers.accountAlreadyExists"));
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const legalReview = async (id, action) => {
    try {
      await api.post(`/suppliers/${id}/legal-review`, { action });
      toast.success(action === "approve" ? t("suppliers.legalApprovedToast") : t("suppliers.legalRejectedToast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const copyCompletionLink = async (s) => {
    let url = s.completion_token ? `${window.location.origin}/completar-registro/${s.completion_token}` : null;
    // Fix WH84 (2026-10-21): un proveedor pendiente sin link (alta vieja) recibe
    // uno nuevo al copiar. No hay link anterior que anular.
    if (!url) {
      try {
        const r = await api.post(`/admin/suppliers/${s.id}/regenerate-completion-token`);
        url = r.data.completion_url;
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("common.error"));
        return;
      }
    }
    navigator.clipboard?.writeText(url);
    toast.success(t("suppliers.completionLinkCopied"));
  };

  // Fix WH84 (2026-10-21): Excel con todos los proveedores que no completaron su
  // registro y su link, para mandárselos (pedido de Compras).
  const pendingLinksCount = list.filter((s) => s.status === "pending_documents" && !s.archived_at).length;
  const downloadPendingLinks = async () => {
    try {
      const r = await api.get("/admin/suppliers/pending-completion-links", { responseType: "blob" });
      downloadBlob(r.data, `links_proveedores_pendientes_${todayStr()}.xlsx`);
      toast.success(t("suppliers.pendingLinksDownloaded", { count: pendingLinksCount }));
    } catch {
      toast.error(t("common.error"));
    }
  };

  const filtered = list.filter((s) => {
    if (filter === "archived") {
      if (!s.archived_at) return false;
    } else {
      if (s.archived_at) return false;
      if (filter !== "all" && s.status !== filter) return false;
    }
    if (onlyPending && !s.needs_branch_operations_setup) return false;
    if (search && !`${s.business_name} ${s.rfc}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const pendingOpsCount = list.filter((s) => s.needs_branch_operations_setup).length;

  return (
    <>
      <Topbar title={t("suppliers.title")} subtitle={t("suppliers.subtitle", { count: list.length })} />
      <div className="p-6 space-y-5">
        {pendingOpsCount > 0 && (
          <div data-testid="branch-ops-pending-counter"
            className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5 text-amber-800">
              <Store className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-semibold">{t("suppliers.opsPendingCounter", { count: pendingOpsCount })}</span>
            </div>
            <button data-testid="branch-ops-pending-counter-link" onClick={() => setOnlyPending(true)}
              className="text-xs font-bold text-amber-900 hover:underline">
              {t("suppliers.opsPendingCounterLink")} →
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="sup-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("suppliers.searchPlaceholder")}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1">
            {[
              { v: "all", l: t("suppliers.filterAll") },
              { v: "pending_documents", l: t("suppliers.filterPendingDocs") },
              { v: "pending_approval", l: t("suppliers.filterPending") },
              { v: "approved", l: t("suppliers.filterApproved") },
              { v: "suspended", l: t("suppliers.filterSuspended") },
              { v: "rejected", l: t("suppliers.filterRejected") },
              ...(user?.role === "corporate_admin" ? [{ v: "archived", l: t("suppliers.filterArchived") }] : []),
            ].map((f) => (
              <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`sup-filter-${f.v}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                  filter === f.v ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
                {f.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1">
            <span className="px-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("suppliers.typeFilterLabel")}</span>
            {[
              { v: "all", l: t("suppliers.typeAll") },
              { v: "insumos", l: t("suppliers.typeInsumos") },
              { v: "servicios", l: t("suppliers.typeServicios") },
            ].map((f) => (
              <button key={f.v} onClick={() => setTypeFilter(f.v)} data-testid={`sup-type-filter-${f.v}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                  typeFilter === f.v ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
                {f.l}
              </button>
            ))}
          </div>
          <label data-testid="sup-only-pending-ops-filter"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold cursor-pointer transition ${
              onlyPending ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}>
            <input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} className="w-3.5 h-3.5" />
            {t("suppliers.opsFilterPending")}
          </label>
          <button data-testid="suppliers-export-btn" onClick={exportToExcel} disabled={exporting}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold disabled:opacity-50">
            <Download className="w-4 h-4" /> {exporting ? t("export.exporting") : t("export.button")}
          </button>
          {user?.role === "corporate_admin" && pendingLinksCount > 0 && (
            <button data-testid="suppliers-pending-links-btn" onClick={downloadPendingLinks}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-xl text-sm font-bold">
              <Download className="w-4 h-4" /> {t("suppliers.pendingLinksButton", { count: pendingLinksCount })}
            </button>
          )}
          {user?.role === "corporate_admin" && (
            <button data-testid="add-supplier-open-btn" onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold">
              <Plus className="w-4 h-4" /> {t("addSupplier.button")}
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <EmptyState title={t("suppliers.noResultsTitle")} message={t("suppliers.noResultsMsg")} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((s, idx) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                className={`bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow ${s.status === "pending_documents" ? "opacity-75" : ""}`}
                data-testid={`supplier-card-${s.rfc || s.id}`} data-supplier-id={s.id}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base text-[#1A1A1A] truncate">{s.business_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{s.rfc || t("suppliers.noRfcYet")}</div>
                    {(() => {
                      const stype = s.supplier_type || "insumos";
                      const isServ = stype === "servicios";
                      const Icon = isServ ? Wrench : Package;
                      return (
                        <span data-testid={`sup-type-badge-${s.rfc}`}
                          className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            isServ ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"
                          }`}>
                          <Icon className="w-3 h-3" /> {isServ ? t("suppliers.typeServicios") : t("suppliers.typeInsumos")}
                        </span>
                      );
                    })()}
                  </div>
                  <StatusBadge status={s.status === "approved" ? "approved_supplier" : s.status} />
                </div>

                <div className="mb-3 flex items-center gap-1.5 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    s.legal_parcial ? "bg-amber-100 text-amber-800" :  // Fix WH65 (2026-09-28)
                    s.legal_review_status === "approved" ? "bg-emerald-100 text-emerald-800" :
                    s.legal_review_status === "rejected" ? "bg-red-100 text-red-700" :
                    "bg-slate-100 text-slate-600"
                  }`} data-testid={`sup-legal-badge-${s.rfc}`}
                    title={s.legal_parcial ? (s.legal_faltantes || []).join(", ") : undefined}>
                    {t("suppliers.legalReview")}: {t(`suppliers.legalStatus.${s.legal_parcial ? "partial" : (s.legal_review_status || "pending")}`)}
                  </span>
                  {(s.branches_authorized || []).length >= 2 && (
                    <span data-testid={`sup-ops-status-badge-${s.rfc || s.id}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        s.needs_branch_operations_setup ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                      }`}>
                      <Store className="w-3 h-3" />
                      {s.needs_branch_operations_setup ? t("suppliers.opsStatusPending") : t("suppliers.opsStatusConfigured")}
                    </span>
                  )}
                </div>

                <div className="space-y-1 text-xs text-slate-600 mb-4">
                  <div className="flex justify-between"><span>{t("common.email")}</span><span className="font-medium truncate ml-2">{s.contact_email || "—"}</span></div>
                  <div className="flex justify-between"><span>{t("common.creditDays")}</span><span className="font-semibold">{s.credit_days} {t("common.creditDays")}</span></div>
                  <div className="flex justify-between"><span>{t("common.bank")}</span><span className="font-medium">{s.bank_name || "—"}</span></div>
                </div>

                {s.authorized_branches?.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t("suppliers.authorizedBranches")}</div>
                    <div className="flex flex-wrap gap-1">
                      {s.authorized_branches.map((b) => (
                        <span key={b.id} className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[10px] rounded font-semibold">{b.name}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                  <button data-testid={`sup-view-btn-${s.rfc || s.id}`} onClick={() => navigate(`/proveedores/${s.id}`)}
                    className="flex-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" /> {t("common.view")}
                  </button>
                  {/* Fix WH84 (2026-10-21): antes solo salía en los de la carga masiva (bulk_loaded);
                      los dados de alta a mano también lo necesitan para reenviarlo. */}
                  {user?.role === "corporate_admin" && s.status === "pending_documents" && (
                    <button data-testid={`sup-copy-link-btn-${s.id}`} onClick={() => copyCompletionLink(s)}
                      className="flex-1 px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5">
                      <Copy className="w-3.5 h-3.5" /> {t("suppliers.copyCompletionLink")}
                    </button>
                  )}
                  {(user?.role === "legal" || user?.role === "corporate_admin") && (
                    <button data-testid={`sup-docs-btn-${s.rfc || s.id}`} onClick={() => openDocs(s)}
                      className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold flex items-center gap-1.5" title={t("suppliers.viewDocuments")}>
                      <FileText className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {user?.role === "legal" && s.status !== "pending_documents" && (
                    <>
                      <button data-testid={`sup-legal-approve-${s.rfc || s.id}`} onClick={() => legalReview(s.id, "approve")}
                        className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> {t("suppliers.legalApprove")}
                      </button>
                      <button data-testid={`sup-legal-reject-${s.rfc || s.id}`} onClick={() => legalReview(s.id, "reject")}
                        className="px-3 py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-xs font-bold flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {user?.role === "corporate_admin" && s.status === "pending_approval" && (
                    <>
                      <button data-testid={`sup-approve-btn-${s.rfc || s.id}`} onClick={() => openApprove(s)}
                        className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> {t("suppliers.approve")}
                      </button>
                      <button onClick={() => decide(s.id, "reject")}
                        className="px-3 py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-xs font-bold flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {user?.role === "corporate_admin" && s.status === "approved" && (
                    <button onClick={() => decide(s.id, "suspend")}
                      className="px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-xs font-bold flex items-center gap-1">
                      <Pause className="w-3.5 h-3.5" /> {t("suppliers.suspend")}
                    </button>
                  )}
                  {user?.role === "corporate_admin" && s.status === "suspended" && (
                    <button onClick={() => decide(s.id, "approve")}
                      className="px-3 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded-lg text-xs font-bold flex items-center gap-1">
                      <Play className="w-3.5 h-3.5" /> {t("suppliers.reactivate")}
                    </button>
                  )}
                  {user?.role === "corporate_admin" && !s.archived_at && (
                    <button data-testid={`sup-archive-btn-${s.rfc || s.id}`}
                      onClick={() => { setToArchive(s); setArchiveReason(""); }}
                      className="px-3 py-2 bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg text-xs font-bold flex items-center gap-1">
                      <Archive className="w-3.5 h-3.5" /> {t("suppliers.archive")}
                    </button>
                  )}
                  {user?.role === "corporate_admin" && s.archived_at && (
                    <button data-testid={`sup-restore-btn-${s.rfc || s.id}`}
                      onClick={() => { setToRestore(s); setRestoreReason(""); }}
                      className="px-3 py-2 bg-slate-800 hover:bg-black text-white rounded-lg text-xs font-bold flex items-center gap-1">
                      <RotateCcw className="w-3.5 h-3.5" /> {t("suppliers.restore")}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {toArchive && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setToArchive(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="supplier-archive-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                <Archive className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-[#1A1A1A]">{t("suppliers.archiveTitle")}</div>
                <div className="text-xs text-slate-500">{toArchive.business_name}{toArchive.rfc ? ` · ${toArchive.rfc}` : ""}</div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-900 leading-relaxed" data-testid="supplier-archive-warning">
                {t("suppliers.archiveWarning")}
              </p>
            </div>
            <label className="block text-xs font-bold text-slate-600 mb-1">{t("suppliers.archiveReason")}</label>
            <textarea data-testid="supplier-archive-reason" value={archiveReason} rows={3}
              onChange={(e) => setArchiveReason(e.target.value)} placeholder={t("suppliers.archiveReasonPh")}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 mb-3" />
            <div className="flex gap-2">
              <button onClick={() => setToArchive(null)} disabled={busy}
                className="flex-1 py-2.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-bold disabled:opacity-50">
                {t("common.cancel")}
              </button>
              <button data-testid="supplier-archive-submit-btn" disabled={busy || archiveReason.trim().length < 10} onClick={submitArchive}
                className="flex-1 py-2.5 bg-slate-800 hover:bg-black text-white rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                <Archive className="w-4 h-4" /> {t("suppliers.archive")}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {toRestore && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setToRestore(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="supplier-restore-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <RotateCcw className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-[#1A1A1A]">{t("suppliers.restore")}</div>
                <div className="text-xs text-slate-500">{toRestore.business_name}</div>
              </div>
            </div>
            <textarea data-testid="supplier-restore-reason" value={restoreReason} rows={3}
              onChange={(e) => setRestoreReason(e.target.value)} placeholder={t("suppliers.archiveReasonPh")}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 mb-3" />
            <div className="flex gap-2">
              <button onClick={() => setToRestore(null)} disabled={busy}
                className="flex-1 py-2.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-bold disabled:opacity-50">
                {t("common.cancel")}
              </button>
              <button data-testid="supplier-restore-submit-btn" disabled={busy || restoreReason.trim().length < 10} onClick={submitRestore}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                <RotateCcw className="w-4 h-4" /> {t("suppliers.restore")}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {approveModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setApproveModal(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="supplier-approve-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-[#1A1A1A]">{t("suppliers.approveModalTitle")}</div>
                <div className="text-xs text-slate-500 truncate">{approveModal.business_name} · {approveModal.rfc}</div>
              </div>
            </div>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">{t("suppliers.approveModalHint")}</p>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("suppliers.loginEmailLabel")}</label>
              <input
                data-testid="supplier-login-email-input"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              />
              <div className="text-[11px] text-slate-400 mt-1.5">{t("suppliers.loginEmailHint")}</div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setApproveModal(null)} disabled={busy}
                className="flex-1 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
                {t("common.cancel")}
              </button>
              <button data-testid="supplier-approve-confirm-btn" onClick={confirmApprove} disabled={busy}
                className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold disabled:opacity-50">
                {t("suppliers.approveAndCreate")}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {docsModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDocsModal(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="supplier-docs-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-base text-[#1A1A1A] truncate">{t("suppliers.documentsTitle")}</div>
                <div className="text-xs text-slate-500 truncate">{docsModal.supplier.business_name} · {docsModal.supplier.rfc}</div>
              </div>
            </div>
            {docsModal.docs === null ? (
              <div className="py-8 text-center text-sm text-slate-500">{t("common.loading")}</div>
            ) : docsModal.docs.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">{t("suppliers.noDocuments")}</div>
            ) : (
              <div className="space-y-2">
                {docsModal.docs.map((doc) => (
                  <div key={doc.id} data-testid={`supplier-doc-row-${doc.doc_type}`}
                    className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-3.5 py-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#1A1A1A]">{t(`supplierReg.docLabels.${doc.doc_type}`)}</div>
                      <div className="text-xs text-slate-500 truncate">{doc.filename}</div>
                    </div>
                    <button data-testid={`supplier-doc-view-${doc.doc_type}`} onClick={() => viewDoc(doc)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A1A] text-white rounded-lg text-xs font-bold hover:bg-black flex-shrink-0">
                      <ExternalLink className="w-3.5 h-3.5" /> {t("suppliers.viewDoc")}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button data-testid="supplier-docs-close-btn" onClick={() => setDocsModal(null)}
              className="w-full mt-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">
              {t("common.close")}
            </button>
          </motion.div>
        </div>
      )}

      {accountModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="supplier-account-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#FFFBE6] border border-[#FFDF00] text-[#1A1A1A] flex items-center justify-center">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-[#1A1A1A]">{t("suppliers.accountCreatedTitle")}</div>
                <div className="text-xs text-slate-500">{t("suppliers.accountCreatedSub")}</div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 mb-4 leading-relaxed">
              {t("suppliers.accountWarning")}
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t("common.email")}</div>
                <div data-testid="supplier-account-email" className="text-sm font-mono bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{accountModal.email}</div>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t("suppliers.initialPassword")}</div>
                <div className="flex items-center gap-2">
                  <div data-testid="supplier-account-password" className="flex-1 text-sm font-mono font-bold bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{accountModal.password_initial}</div>
                  <button
                    data-testid="supplier-account-copy-btn"
                    onClick={() => { navigator.clipboard?.writeText(`${accountModal.email} / ${accountModal.password_initial}`); toast.success(t("suppliers.accountCopied")); }}
                    className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0">
                    <Copy className="w-4 h-4 text-slate-600" />
                  </button>
                </div>
              </div>
            </div>
            <button
              data-testid="supplier-account-close-btn"
              onClick={() => setAccountModal(null)}
              className="btn-primary-chizy w-full mt-5 py-2.5 rounded-xl text-sm">
              {t("suppliers.accountUnderstood")}
            </button>
          </motion.div>
        </div>
      )}
      {showAddModal && (
        <AddSupplierModal onClose={() => setShowAddModal(false)} onCreated={load} />
      )}
    </>
  );
}
